import React, { memo, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useStyles } from '../../../LawPortal.styles';
import {
  MTable,
  Transition
} from '../../../../../Components';
import { useIntl } from 'react-intl';
import {
  Button,
  Grid,
  Tooltip,
} from '@material-ui/core';
import {
  ArchiveOutlined,
  ArrowRightAlt,
  Delete,
  Edit,
  UnarchiveOutlined,
} from '@material-ui/icons';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import {
  AccessLevel,
  LawPortalFeature,
  Module,
  UserContext
} from "../../../../../Context/UserContext/UserContext";
import { useAptorApi } from "../../../../../Api";
import PlanningViewLK from "./View/PlanningViewLK";
import moment from "moment";
import { useComplianceState } from "./List/ComplianceState";
import { useNavigate, useLocation } from "react-router-dom";
import { SelectField } from '../../../../../Components/Field';
import { useSnackbar } from 'notistack';
import { useConfirmation } from '../../../../../Context/ConfirmationContext/ConfirmationContext';

// Memoized IconsNode component
const IconsNode = memo(({ planData, activeTab, onArchive, onUnarchive, onEdit, onDelete, onComplianceSession, formatMessage,hasAccessToModule }: any) => {
    const classes = useStyles();
  return (
    <>
      {activeTab === 'complete' ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '7px' }}>
          <Tooltip title={formatMessage({ id: "law-portal.unarchive" })}>
            <div onClick={() => onUnarchive(planData)}>
              <UnarchiveOutlined style={{ cursor: 'pointer' }} />
            </div>
          </Tooltip>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
         {(hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization))&& <div style={{ width: 28, display: 'inline-flex', justifyContent: 'center' }}>
            <Tooltip title={formatMessage({ id: 'law-portal.legal.plan.edit' })}>
              <div style={{ cursor: 'pointer', display: 'inline-flex', padding: '4px' }} onClick={(e) => { e.stopPropagation(); onEdit(planData); }}>
                <Edit fontSize="small" />
              </div>
            </Tooltip>
          </div>}
          {(hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization))&&<div style={{ width: 28, display: 'inline-flex', justifyContent: 'center' }}>
            <Tooltip title={formatMessage({ id: 'law-portal.legal.plan.delete' })}>
              <div style={{ cursor: 'pointer', display: 'inline-flex', padding: '4px' }} onClick={(e) => { e.stopPropagation(); onDelete(planData); }}>
                <Delete fontSize="small" />
              </div>
            </Tooltip>
          </div>}
          <div style={{ width: 28, display: 'inline-flex', justifyContent: 'center' }}>
            <Tooltip title={formatMessage({ id: "law-portal.archive" })}>
              <div style={{ cursor: 'pointer', display: 'inline-flex', padding: '4px' }} onClick={(e) => { e.stopPropagation(); onArchive(planData); }}>
                <ArchiveOutlined fontSize="small" />
              </div>
            </Tooltip>
          </div>
          {planData?.lawCount > 0 && planData.isExecutionVisible && (
            <Button 
              variant="contained" 
              type="submit" 
              size="small"
              className={classes.executeButton}
              style={{ whiteSpace: 'nowrap', marginLeft: '4px' }}
              onClick={(event: any) => onComplianceSession(event, planData)}
            >
              <ArrowRightAlt />
              {formatMessage({ id: "law-portal.compliance-law.execute.evaluation-for-all" })}
            </Button>
          )}
        </div>
      )}
    </>
  );
});

export const CarryLK = () => {
  const {
    startComplianceSession,
    setLawsSelectedForCompliance,
    toggleCompliance,
    setPlan,
    setPLanEdit
  } = useComplianceState();

  const classes = useStyles();
  const { customerId, firstName, lastName, hasAccessToModule, hasAccessToFeature } = useContext(UserContext);
  const { api, abortController } = useAptorApi();
  const [directPlanLoading, setDirectPlanLoading] = useState(false);
  const [planData, setPlanData] = useState<any[]>([]);
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [archivePlan, setArchivePlan] = useState<any[]>([]);
  const { deleteConfirmation } = useConfirmation();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState<string>('ongoing-plan');
  const tableRef = React.createRef();
  const [startCompliance, setStartCompliance] = useState(false);

  // Memoize current user name
  const currentuserName = useMemo(() => 
    `${firstName} ${lastName}`, 
    [firstName, lastName]
  );

  useEffect(() => {
    setHeader(
      formatMessage({ id: 'law-portal.legal.compliance.header', defaultMessage: 'Evaluation control' }),
    );
  }, [setHeader, formatMessage]);

  // Memoize confirmation messages
  const archiveconfirmation = useMemo(() => ({
    title: formatMessage({ id: 'component.plan-change.archive.label' }),
    description: formatMessage({ id: 'component.plan-change.archive.confirmation' }),
  }), [formatMessage]);

  const unarchiveconfirmation = useMemo(() => ({
    title: formatMessage({ id: 'component.plan-change.archive.label' }),
    description: formatMessage({ id: 'component.plan-change.unarchive.confirmation' }),
  }), [formatMessage]);

  // Memoized callbacks
  const getplanLaws = useCallback(async (planData: any) => {
    const response: any = await api.getPlanLawsexecute(planData.id);
    if (response?.length === 0 || response == null) {
      enqueueSnackbar(
        formatMessage({ id: 'evelution-all-error-msg' }, { companyunitname: planData?.customerCompanyUnitId?.name }), 
        { variant: 'error' }
      );
    }
    setPlan(planData);
    setLawsSelectedForCompliance(response);
    setStartCompliance(true);
  }, [api, enqueueSnackbar, formatMessage, setPlan, setLawsSelectedForCompliance]);

  const getPlanRequirement = useCallback(async (planData: any) => {
    const response: any = await api.getPlanrequirementexecute(planData.id);
    if (response?.length === 0 || response == null) {
      enqueueSnackbar(
        formatMessage({ id: 'evelution-all-error-msg' }, { companyunitname: planData?.customerCompanyUnitId?.name }), 
        { variant: 'error' }
      );
    }
    setPlan(planData);
    setLawsSelectedForCompliance(response);
    setStartCompliance(true);
  }, [api, enqueueSnackbar, formatMessage, setPlan, setLawsSelectedForCompliance]);

  const handleComplianceSession = useCallback(async (event: any, planData: any) => {
    event.stopPropagation();
    if (planData?.type === 1) {
      await getPlanRequirement(planData);
    } else {
      await getplanLaws(planData);
    }
    setStartCompliance(false);
  }, [getPlanRequirement, getplanLaws]);

  useEffect(() => {
    startComplianceSession();
  }, [startCompliance, startComplianceSession]);



  const getComplianceDirectPlan = useCallback(async () => {
    setDirectPlanLoading(true);
    try {
      const response: any = await api.getAllcompliancePlan(customerId, { filters: {}, search: '' });
      
      if (!abortController.current.signal.aborted) {
        const sortData = (arr: any[]) => {
          return arr?.sort((a: any, b: any) => {
            const startDateComparison = new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            if (startDateComparison === 0) {
              return a.name.localeCompare(b.name);
            }
            return startDateComparison;
          }) || [];
        };

        const allActivePlans = response?.customerPlans?.filter((val: any) => val.isActive && !val.isArchived) || [];
        const archived = response?.customerPlans?.filter((val: any) => val.isArchived) || [];

        setPlanData(sortData(allActivePlans));
        setArchivePlan(sortData(archived));
      }
    } catch (error) {
      console.error('Error fetching compliance plans:', error);
    } finally {
      setDirectPlanLoading(false);
    }
  }, [api, customerId, abortController]);

  const handleArchive = useCallback((planData: any) => {
    const onReplace = async () => {
      await api.updatePlan(planData?.id, { isArchived: true })
      return getComplianceDirectPlan()
    };

    const onCancel = () => { };

    deleteConfirmation(
      onReplace,
      archiveconfirmation,
      onCancel,
    );
  }, [api, getComplianceDirectPlan, deleteConfirmation, archiveconfirmation]);

  const handleEditPlan = useCallback((planData: any) => {
    // Extract only serializable fields to avoid DataCloneError with navigate state
    const editPlan = {
      id: planData.id ?? planData._planId,
      name: planData.name,
      customerCompanyUnitId: planData.customerCompanyUnitId,
      startDate: planData.startDate,
      endDate: planData.endDate,
      type: planData.type,
      reminder: planData.reminder,
      recurrentInterval: planData.recurrentInterval,
      isDirect: planData.isDirect,
      legislationId: planData.legislationId,
      legislationName: planData.legislationName,
    };
    navigate('/law-portal/legal-compliance-control/planning', { state: { editPlan } });
  }, [navigate]);

const handleDeletePlan = useCallback(async (data: any) => {
    try {
      const res = await api.deletePlan(data?.id);
      if (res.errorMessage) {
        enqueueSnackbar(formatMessage({ id: res.errorMessage }), { variant: 'error' });
      } else {
        await getComplianceDirectPlan();
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  }, [api, enqueueSnackbar, formatMessage, getComplianceDirectPlan]);

  const handleunArchive = useCallback((planData: any) => {
    const onReplace = async () => {
      await api.updatePlan(planData?.id, { isArchived: false })
      return getComplianceDirectPlan()
    };

    const onCancel = () => { };

    deleteConfirmation(
      onReplace,
      unarchiveconfirmation,
      onCancel,
    );
  }, [api, getComplianceDirectPlan, deleteConfirmation, unarchiveconfirmation]);

  const handleComplianceSessionnew = useCallback(async (event: any, lawData: any) => {
    let lawDatanew = { planid: lawData.id, ...lawData };
    lawDatanew.id = lawData?.legislationId;
    setStartCompliance(true);
    event.stopPropagation();
    setPlan(lawData);
    await toggleCompliance(lawDatanew);
    setStartCompliance(false);
  }, [setPlan, toggleCompliance]);

  const handleEditSession = useCallback(async (lawData: any) => {
    let lawDatanew = { planid: lawData.id, ...lawData };
    lawDatanew.id = lawData?.legislationId;
    setStartCompliance(true);
    setPlan(lawData);
    setPLanEdit(true);
    await toggleCompliance(lawDatanew);
    setStartCompliance(false);
  }, [setPlan, setPLanEdit, toggleCompliance]);

  const handlemsg = useCallback(() => {
    enqueueSnackbar(formatMessage({ id: 'component.law-error-description' }), {
      variant: 'error',
    });
  }, [enqueueSnackbar, formatMessage]);

  useEffect(() => {
    getComplianceDirectPlan();
  }, [getComplianceDirectPlan]);

  // Column definitions for regular tabs
  const regularPlanColumns = [
    { 
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), 
      field: 'name',
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'customerCompanyUnitId',
      render: (row: any) => row?.customerCompanyUnitId?.name,
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: any) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }), 
      field: 'endDate',
      render: (row: any) => row?.endDate ? moment(row?.endDate).format('YYYY-MM-DD') : "-", 
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: any) => row?.recurrentInterval || "-",
      sorting: false,
    },
    {
      title: formatMessage({ id: 'utils.filters.status' }),
      field: 'status',
      render: (row: any) => `${row.executedLawCount} ${formatMessage({ id: 'text.of' })} ${row.lawCount} ${formatMessage({ id: 'status.text' })} `,
      sorting: false,
    },
    {
      title: "",
      field: '',
      width: 'auto',
      cellStyle: { whiteSpace: 'nowrap' as const, padding: '4px 8px', maxWidth: 'none' },
      headerStyle: { whiteSpace: 'nowrap' as const, padding: '4px 8px', maxWidth: 'none' },
      render: (row: any) => (
        <IconsNode 
          planData={row} 
          activeTab={activeTab}
          onArchive={handleArchive}
          onUnarchive={handleunArchive}
          onEdit={handleEditPlan}
          onDelete={handleDeletePlan}
          onComplianceSession={handleComplianceSession}
          formatMessage={formatMessage}
          hasAccessToModule={hasAccessToModule}
        />
      ),
      sorting: false,
      hiddenByColumnsButton:true
    },
  ];

  // Determine current columns and data based on activeTab
  const currentColumns = regularPlanColumns;
  const currentData = activeTab === 'complete' ? archivePlan : planData;

  const statusOptions = useMemo(() => [
    { value: 'ongoing-plan', label: `${formatMessage({ id: 'law-portal.compliance.tab.carryout.heading.continuously' })} (${planData?.length ?? 0})` },
    { value: 'complete', label: `${formatMessage({ id: 'lawportal.evalution.completed.compliance.tab' })} (${archivePlan?.length ?? 0})` },
  ], [formatMessage, planData?.length, archivePlan?.length]);

  // Handler for starting compliance from detail panel
  const handleDetailStartCompliance = useCallback((lawData: any, plan: any) => {
    setPlan(plan);
    toggleCompliance(lawData);
    startComplianceSession();
  }, [setPlan, toggleCompliance, startComplianceSession]);

  // Handler for editing compliance from detail panel
  const handleDetailEditCompliance = useCallback((lawData: any, plan: any) => {
    setPlan(plan);
    setPLanEdit(true);
    toggleCompliance(lawData);
    startComplianceSession();
  }, [setPlan, setPLanEdit, toggleCompliance, startComplianceSession]);

  // Detail panel
  const detailPanel = [
    {
      tooltip: formatMessage({ id: "text.expend" }),
      render: (rowData: any) => (
        <PlanningViewLK 
          planData={rowData?.rowData || rowData} 
          filters={{}} 
          search=""
          activeTab={activeTab}
          onStartCompliance={handleDetailStartCompliance}
          onEditCompliance={handleDetailEditCompliance}
        />
      ),
    }
  ];

  /*
  const ToolbarComponent = useCallback((props: any) => (
    <div className={classes.toolbar} style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex' }}>
        <div
          className={`${classes.tabsize} ${activeTab === "ongoing-plan" ? classes?.tabActive : classes?.tab}`}
          onClick={() => handleTabClick('ongoing-plan')}
          style={{ cursor: 'pointer', width: '135px' }}
        >
          {formatMessage({ id: 'law-portal.compliance.tab.carryout.heading.continuously' })}
          <span className={classes?.tabNumber}>{planData?.length}</span>
        </div>
        <div
          className={`${classes.tabsize} ${activeTab === "complete" ? classes?.tabActive : classes?.tab}`}
          onClick={() => handleTabClick('complete')}
          style={{ cursor: 'pointer', width: '135px' }}
        >
          {formatMessage({ id: 'lawportal.evalution.completed.compliance.tab' })}
          <span className={classes?.tabNumber}>{archivePlan?.length}</span>
        </div>
      </div>
      <MTableToolbar {...props} />
    </div>
  ), [activeTab, classes, planData, archivePlan, formatMessage, handleTabClick]);
  */

  return (
    <div className={classes.listWrapper}>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4} md={3}>
          <SelectField
            name="status"
            options={statusOptions}
            initialState={{ value: activeTab, label: statusOptions.find(option => option.value === activeTab)?.label }}
            isClearable={false}
            errorTexts={[]}
            onChange={value => {
              if ((value?.value === 'ongoing-plan' || value?.value === 'complete') && value.value !== activeTab) {
                setActiveTab(value.value);
              }
            }}
          />
        </Grid>
      </Grid>
      {/* <hr style={{ marginTop: '25px', marginBottom: "25px" }} /> */}
      <Transition isLoading={directPlanLoading}>
        <MTable
          key={activeTab}
          tableRef={tableRef}
          columns={currentColumns}
          // Previous toolbar: components={{ Toolbar: ToolbarComponent }}
          data={currentData}
          options={{
            detailPanelColumnAlignment: 'right',
            pageSizeOptions: [5, 10, 20, 50, 100],
            debounceInterval: 500,
          }}
          detailPanel={detailPanel}
        />
      </Transition>
    </div>
  );
};